import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { journeyApi } from '../../api/journeyApi';
import { journeyPublicationApi } from '../../api/journeyPublicationApi';
import { journeyMemoryApi } from '../../api/journeyMemoryApi';
import { journeyPlanningApi } from '../../api/journeyPlanningApi';
import { travelMediaApi } from '../../api/travelMediaApi';
import { useAuth } from '../../auth/AuthContext';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { collectMemberIds, memberDisplayNameWithId } from '../member-display/memberDisplayModel';
import SafeImage from '../../components/SafeImage';
import { ConfirmDialog } from '../../components/ui';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import { useTravelText } from '../../app/TravelLocaleContext';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import { normalizePlanningSchedule } from '../journey-planning/planningModel';
import {
  MEMORY_MEDIA_UI_LIMIT,
  MEMORY_TYPES,
  MEMORY_VISIBILITIES,
  MEDIA_UPLOAD_STATUS,
  activitiesForDay,
  applyActivitySelection,
  applyDaySelection,
  buildCasConfirmPayload,
  buildCreateMemoryPayload,
  buildUpdateMemoryPayload,
  buildVisibilityPayload,
  canWriteMemory,
  classifyMemoryError,
  createLocalMediaId,
  createMemoryClientRequestId,
  dayChangeClearsActivity,
  draftFromMemory,
  emptyMemoryDraft,
  findActivityInSchedule,
  formatActivityOptionLabel,
  formatDayOptionLabel,
  hasUploadingMedia,
  isMemoryAuthor,
  isMemoryImageObjectKey,
  MEMORY_MEDIA_KIND,
  mediaSlotsFromMemory,
  memoryMediaSlotKind,
  memoryWithImageMediaOnly,
  memoryTypeLabel,
  memoryVisibilityHint,
  memoryVisibilityLabel,
  moveMediaSlot,
  normalizeMediaDisplayOrder,
  normalizeMemoryPage,
  privateMemoryMediaAccessNote,
  revokeAllMediaPreviews,
  revokeMediaPreview,
  validateImageFile,
  validateMemoryDraft,
} from './journeyMemoryModel';
import {
  canShareMemoryToChat,
  canShareMemoryToSocial,
  linkedJourneyPublicationIdFromManagement,
  MEMORY_CHAT_SHARE_CONFIRM,
  MEMORY_CHAT_SHARE_PRIVATE_DISCLOSURE,
} from './memoryShareModel';
import MemoryShareComposer from './MemoryShareComposer';
import MemoryStoryConsent from './MemoryStoryConsent';
import MemoryStoryPreview from './MemoryStoryPreview';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import './journey-journal.css';

function mutationMessage(kind, detail, fallback) {
  if (kind === 'conflict') return '這篇日記已被更新。';
  if (kind === 'forbidden') return '你沒有權限執行此操作。';
  if (kind === 'lifecycle') return detail || '目前旅程狀態不允許寫入日記。';
  if (kind === 'validation') return detail || '請檢查輸入內容。';
  if (kind === 'idempotency-conflict') {
    return detail || '這次建立請求與先前內容衝突，請調整後重試。';
  }
  if (kind === 'not-found') return detail || '日記不存在。';
  if (kind === 'unauthorized') return '請重新登入。';
  return detail || fallback;
}

function mediaGridClass(count) {
  if (count <= 1) return 'journey-journal__media-grid is-one';
  if (count === 2) return 'journey-journal__media-grid is-two';
  return 'journey-journal__media-grid is-many';
}

/** 影片與無法辨識的媒體只顯示文字佔位：不渲染圖片、不送公共代理、不建立播放器。 */
function MemoryMediaPlaceholder({ kind, className = 'journey-journal__media-unsupported' }) {
  const { t } = useTravelText();
  return (
    <span className={className} data-testid="memory-media-placeholder" data-media-kind={kind}>
      {t(kind === MEMORY_MEDIA_KIND.VIDEO ? 'travel.journal.media.videoUnsupported' : 'travel.journal.media.unsupported')}
    </span>
  );
}

function MemoryArticle({
  journeyId,
  entry,
  viewerMemberId,
  authorLabel,
  canMutateJourney,
  busy,
  sharingToChat,
  onEdit,
  onShare,
  onShareToChat,
  onHide,
  onRestore,
  onDelete,
  onVisibility,
}) {
  const mine = isMemoryAuthor(entry, viewerMemberId);
  const canMutate = mine && canMutateJourney && !entry.isDeleted;
  const canShare = canShareMemoryToSocial(entry, viewerMemberId) && canMutateJourney;
  const canChatShare = canShareMemoryToChat(entry, viewerMemberId) && canMutateJourney;
  // 收錄授權不依旅程可寫狀態顯示：完成／封存後作者仍須能撤回。
  const showStoryConsent = mine && !entry.isDeleted && entry.memoryType !== 'PRIVATE_NOTE' && Boolean(journeyId);

  return (
    <article className="journey-journal__article" aria-labelledby={`memory-${entry.memoryEntryId}`}>
      <h4 className="journey-journal__title" id={`memory-${entry.memoryEntryId}`}>
        {entry.title || entry.memoryTypeLabel}
      </h4>
      <p className="journey-journal__meta">
        <span className={`journey-journal__badge ${entry.isPrivate ? 'is-private' : ''} ${entry.isPublic ? 'is-public' : ''} ${entry.isHidden ? 'is-hidden' : ''}`}>
          {entry.visibilityLabel}
          <span className="visually-hidden"> ({entry.visibility})</span>
        </span>
        <span className="journey-journal__badge">
          {entry.statusLabel}
          <span className="visually-hidden"> ({entry.status})</span>
        </span>
        <span>{entry.memoryTypeLabel}</span>
        {entry.occurredAt ? (
          <time dateTime={entry.occurredAt}>{formatDateTime(entry.occurredAt)}</time>
        ) : null}
        {mine ? <span>我的日記</span> : <span className="journey-journal__author">{authorLabel}</span>}
        {entry.updateTime ? <span>更新 {formatDateTime(entry.updateTime)}</span> : null}
      </p>
      {entry.daySnapshotLabel ? (
        <p className="journey-journal__snapshot">{entry.daySnapshotLabel}</p>
      ) : null}
      {entry.activitySnapshotLabel ? (
        <p className="journey-journal__snapshot">{entry.activitySnapshotLabel}</p>
      ) : null}
      {entry.isPrivate && !entry.hasMedia ? (
        <p className="journey-journal__privacy" role="status">只有自己可見</p>
      ) : null}
      {entry.isPrivate && entry.hasMedia ? (
        <p className="journey-journal__privacy-note" role="status">{privateMemoryMediaAccessNote()}</p>
      ) : null}
      {entry.hasMedia ? (
        <ul className={mediaGridClass(entry.media.length)} aria-label="日記圖片">
          {entry.media.map((item) => (
            <li key={item.mediaId || `${item.objectKey}-${item.displayOrder}`}>
              {item.kind === MEMORY_MEDIA_KIND.IMAGE ? (
                <SafeImage
                  className="journey-journal__media-img"
                  src={item.url}
                  alt={entry.title ? `${entry.title} 圖片` : '日記圖片'}
                />
              ) : (
                <MemoryMediaPlaceholder kind={item.kind} />
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {entry.content ? <p className="journey-journal__body">{entry.content}</p> : null}
      {canMutate || canShare || canChatShare ? (
        <div className="journey-journal__actions">
          {entry.isActive ? (
            <>
              {canMutate ? (
                <button type="button" disabled={busy} onClick={() => onEdit(entry)}>編輯</button>
              ) : null}
              {canShare ? (
                <button type="button" disabled={busy} onClick={() => onShare(entry)}>
                  分享到動態
                </button>
              ) : null}
              {canChatShare ? (
                <button type="button" disabled={busy || sharingToChat} onClick={() => onShareToChat(entry)}>
                  {sharingToChat ? '分享中…' : '分享到旅程聊天室'}
                </button>
              ) : null}
              {canMutate && entry.memoryType !== 'PRIVATE_NOTE' ? (
                entry.isPrivate ? (
                  <button type="button" disabled={busy} onClick={() => onVisibility(entry, 'PUBLIC')}>
                    改為公開
                  </button>
                ) : (
                  <button type="button" disabled={busy} onClick={() => onVisibility(entry, 'PRIVATE')}>
                    改為私人
                  </button>
                )
              ) : null}
              {canMutate ? (
                <>
                  <button type="button" disabled={busy} onClick={() => onHide(entry)}>隱藏</button>
                  <button type="button" disabled={busy} onClick={() => onDelete(entry)}>刪除日記</button>
                </>
              ) : null}
            </>
          ) : null}
          {entry.isHidden && canMutate ? (
            <>
              <button type="button" disabled={busy} onClick={() => onRestore(entry)}>恢復</button>
              <button type="button" disabled={busy} onClick={() => onDelete(entry)}>刪除日記</button>
            </>
          ) : null}
        </div>
      ) : null}
      {showStoryConsent ? (
        <MemoryStoryConsent
          journeyId={journeyId}
          entry={entry}
          canGrant={canMutateJourney && entry.isActive}
        />
      ) : null}
    </article>
  );
}

export default function JourneyJournalPage() {
  const { t: travelT } = useTravelText();
  // 工作區頁首共用文案（TRAVEL-V2-I18N）：缺 key 時回退既有繁中
  const chromeText = (name) => {
    const key = `travel.journeyWorkspace.chrome.${name}`;
    const value = travelT(key);
    return value === key ? WORKSPACE_PRODUCT_CHROME[name] : value;
  };
  const { journeyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);
  const createRequestIdRef = useRef(null);
  const mediaSlotsRef = useRef([]);
  const fileInputRef = useRef(null);
  const shareGeneration = useRef(0);
  const composeApplied = useRef('');
  const chatShareLock = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [journey, setJourney] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [visible, setVisible] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [mode, setMode] = useState('view'); // view | create | edit
  const [draft, setDraft] = useState(null);
  const [mediaSlots, setMediaSlots] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [needsReload, setNeedsReload] = useState(false);
  const [confirmVisibility, setConfirmVisibility] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [associationHint, setAssociationHint] = useState('');
  const [shareEntry, setShareEntry] = useState(null);
  // 分享封面只取圖片；以 useMemo 維持物件身分，避免分享元件因 entry 變更而重設草稿
  const shareEntryImagesOnly = useMemo(() => memoryWithImageMediaOnly(shareEntry), [shareEntry]);
  const [shareNotice, setShareNotice] = useState('');
  const [journeyPublicationId, setJourneyPublicationId] = useState(null);
  const [publicationProbePending, setPublicationProbePending] = useState(false);
  const [sharingMemoryId, setSharingMemoryId] = useState(null);
  const [chatSharePending, setChatSharePending] = useState(null);
  const [chatShareNotice, setChatShareNotice] = useState('');
  const memberSummaries = useMemberDisplayNames(collectMemberIds([
    ...(journey?.members || []).map((member) => member.memberId),
    ...visible.map((entry) => entry.authorMemberId),
    ...hidden.map((entry) => entry.authorMemberId),
  ]));

  const titleId = useId();
  const contentId = useId();
  const occurredId = useId();
  const typeId = useId();
  const dayId = useId();
  const activityId = useId();
  const fileId = useId();
  const formErrorId = useId();

  useEffect(() => {
    mediaSlotsRef.current = mediaSlots;
  }, [mediaSlots]);

  const clearMediaSlots = useCallback(() => {
    revokeAllMediaPreviews(mediaSlotsRef.current);
    mediaSlotsRef.current = [];
    setMediaSlots([]);
  }, []);

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    const scopedJourneyId = String(journeyId || '');
    if (!soft) {
      setPhase('loading');
      setError('');
      setMode('view');
      setDraft(null);
      setEditing(null);
      clearMediaSlots();
      setConfirmVisibility(null);
      setConfirmDelete(null);
      setFormError('');
      setNeedsReload(false);
      setAssociationHint('');
      setShareEntry(null);
      setShareNotice('');
      setChatShareNotice('');
      setJourneyPublicationId(null);
      setPublicationProbePending(false);
    }
    try {
      const [detailPayload, listPayload, hiddenPayload, schedulePayload] = await Promise.all([
        journeyApi.getDetail(scopedJourneyId),
        journeyMemoryApi.list(scopedJourneyId, { limit: 50 }),
        journeyMemoryApi.listMine(scopedJourneyId, { status: 'HIDDEN', limit: 50 }),
        journeyPlanningApi.getSchedule(scopedJourneyId).catch(() => null),
      ]);
      if (requestId !== generation.current) return;
      if (String(journeyId) !== scopedJourneyId) return;
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      setVisible(normalizeMemoryPage(listPayload).items);
      setHidden(normalizeMemoryPage(hiddenPayload).items);
      setSchedule(schedulePayload ? normalizePlanningSchedule(schedulePayload) : { days: [], unscheduledActivities: [] });
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyMemoryError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else if (kind === 'unauthorized') {
        setError('請重新登入。');
        setPhase('error');
      } else {
        setError('無法載入日誌。');
        setPhase('error');
      }
    }
  }, [journeyId, viewerMemberId, clearMediaSlots]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => {
      generation.current += 1;
      shareGeneration.current += 1;
      composeApplied.current = '';
      revokeAllMediaPreviews(mediaSlotsRef.current);
    };
  }, [journeyId, load]);

  const allowWrite = canWriteMemory({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  });

  useEffect(() => {
    if (phase !== 'ready' || !allowWrite) return;
    if (searchParams.get('compose') !== '1') return;
    const key = searchParams.toString();
    if (composeApplied.current === key) return;
    composeApplied.current = key;
    let next = emptyMemoryDraft();
    const dayId = searchParams.get('journeyDayId') || '';
    const activityIdParam = searchParams.get('activityId') || '';
    if (dayId) next = applyDaySelection(next, dayId);
    if (activityIdParam) {
      const activity = findActivityInSchedule(schedule, activityIdParam);
      if (activity) next = applyActivitySelection(next, activity);
    }
    createRequestIdRef.current = null;
    clearMediaSlots();
    setDraft(next);
    setEditing(null);
    setMode('create');
    setFormError('');
    setNeedsReload(false);
    setAssociationHint('');
  }, [phase, allowWrite, searchParams, schedule, clearMediaSlots]);

  const focusMemoryId = searchParams.get('memory') || '';
  useEffect(() => {
    if (phase !== 'ready' || !focusMemoryId) return undefined;
    const node = document.getElementById(`memory-${focusMemoryId}`);
    node?.scrollIntoView({ block: 'center' });
    return undefined;
  }, [phase, focusMemoryId, visible, hidden]);

  const activityOptions = activitiesForDay(schedule, draft?.journeyDayId);
  const saveBlocked = busy || hasUploadingMedia(mediaSlots);

  async function runMutation(work, { afterSuccess } = {}) {
    if (mutating.current) return;
    const requestId = generation.current;
    mutating.current = true;
    setBusy(true);
    setFormError('');
    setNeedsReload(false);
    try {
      await work();
      if (requestId !== generation.current) return;
      setConfirmVisibility(null);
      setConfirmDelete(null);
      setMode('view');
      setDraft(null);
      setEditing(null);
      clearMediaSlots();
      setAssociationHint('');
      if (afterSuccess) afterSuccess();
      await load({ soft: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyMemoryError(cause);
      setFormError(mutationMessage(kind, cause?.raw?.message || cause?.message, '操作失敗。'));
      setNeedsReload(kind === 'conflict');
      if (kind === 'idempotency-conflict') {
        createRequestIdRef.current = createMemoryClientRequestId();
      }
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  function startCreate() {
    createRequestIdRef.current = null;
    clearMediaSlots();
    setDraft(emptyMemoryDraft());
    setEditing(null);
    setMode('create');
    setFormError('');
    setNeedsReload(false);
    setAssociationHint('');
  }

  function startEdit(entry) {
    clearMediaSlots();
    setEditing(entry);
    setDraft(draftFromMemory(entry));
    setMediaSlots(mediaSlotsFromMemory(entry));
    setMode('edit');
    setFormError('');
    setNeedsReload(false);
    setAssociationHint('');
    setShareEntry(null);
  }

  async function startShare(entry) {
    if (!canShareMemoryToSocial(entry, viewerMemberId) || !allowWrite) return;
    const requestId = ++shareGeneration.current;
    setShareNotice('');
    setShareEntry(entry);
    setJourneyPublicationId(null);
    setPublicationProbePending(true);
    setMode('view');
    setDraft(null);
    setEditing(null);
    clearMediaSlots();
    try {
      if (viewerMemberId == null || viewerMemberId === '') {
        if (requestId !== shareGeneration.current) return;
        setJourneyPublicationId(null);
        return;
      }
      const payload = await journeyPublicationApi.getByJourney(journeyId);
      if (requestId !== shareGeneration.current) return;
      setJourneyPublicationId(linkedJourneyPublicationIdFromManagement(payload));
    } catch {
      if (requestId !== shareGeneration.current) return;
      setJourneyPublicationId(null);
    } finally {
      if (requestId === shareGeneration.current) {
        setPublicationProbePending(false);
      }
    }
  }

  function closeShare() {
    shareGeneration.current += 1;
    setShareEntry(null);
    setPublicationProbePending(false);
  }

  function requestShareMemoryToChat(entry) {
    if (chatShareLock.current || !canShareMemoryToChat(entry, viewerMemberId) || !allowWrite) return;
    setChatSharePending(entry);
  }

  async function confirmShareMemoryToChat() {
    const entry = chatSharePending;
    if (!entry || chatShareLock.current || !canShareMemoryToChat(entry, viewerMemberId) || !allowWrite) return;
    chatShareLock.current = true;
    setSharingMemoryId(entry.memoryEntryId);
    setChatShareNotice('');
    setFormError('');
    try {
      await journeyMemoryApi.shareToChat(journey.journeyId, entry.memoryEntryId, { confirm: true });
      setChatShareNotice(`「${entry.title || entry.memoryTypeLabel}」已分享到旅程聊天室`);
      setChatSharePending(null);
    } catch (cause) {
      const kind = classifyMemoryError(cause);
      const detail = cause?.raw?.message || cause?.message;
      if (kind === 'forbidden') setFormError('你沒有權限分享這篇日記。');
      else if (kind === 'lifecycle') setFormError(detail || '目前旅程狀態不允許分享。');
      else if (kind === 'unavailable' || cause?.code === 95104) {
        setFormError(detail || '聊天室目前無法使用，請稍後再試。');
      } else setFormError(detail || '分享到旅程聊天室失敗。');
    } finally {
      chatShareLock.current = false;
      setSharingMemoryId(null);
    }
  }

  function onSharePublished(result) {
    const requestId = generation.current;
    const postId = result?.socialPostId;
    setShareEntry(null);
    setShareNotice(result?.replayed
      ? '已回到先前建立的動態。'
      : '動態已發布。原日記可見範圍未改變。');
    if (requestId !== generation.current) return;
    if (!postId) return;
    navigate(`/social/posts/${postId}`);
  }

  function cancelForm() {
    setMode('view');
    setDraft(null);
    setEditing(null);
    clearMediaSlots();
    setFormError('');
    setAssociationHint('');
  }

  function onDayChange(nextDayId) {
    setDraft((current) => {
      const next = dayChangeClearsActivity(current, nextDayId, schedule);
      if (next.activityCleared) {
        setAssociationHint('已清除不相容的活動連結。');
      } else {
        setAssociationHint('');
      }
      const { activityCleared, ...rest } = next;
      return rest;
    });
  }

  function onActivityChange(nextActivityId) {
    const activity = findActivityInSchedule(schedule, nextActivityId);
    setDraft((current) => applyActivitySelection(current, activity || null));
    setAssociationHint('');
  }

  async function uploadSlot(localId, file, requestGeneration) {
    setMediaSlots((current) => current.map((slot) => (
      slot.localId === localId
        ? { ...slot, status: MEDIA_UPLOAD_STATUS.UPLOADING, error: '' }
        : slot
    )));
    try {
      const objectKey = await travelMediaApi.uploadImage(file);
      if (requestGeneration !== generation.current) {
        return;
      }
      const key = String(objectKey || '').trim();
      // 圖片上傳只接受合法圖片 key；不符合時標為失敗，保留移除與重試，不變成無法移除的媒體
      if (!key || /:\/\//.test(key) || key.startsWith('blob:') || !isMemoryImageObjectKey(key)) {
        throw new Error('Upload did not return a valid objectKey.');
      }
      setMediaSlots((current) => current.map((slot) => {
        if (slot.localId !== localId) return slot;
        revokeMediaPreview(slot);
        return {
          ...slot,
          status: MEDIA_UPLOAD_STATUS.UPLOADED,
          objectKey: key,
          previewUrl: '',
          error: '',
          file: null,
        };
      }));
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setMediaSlots((current) => current.map((slot) => (
        slot.localId === localId
          ? {
            ...slot,
            status: MEDIA_UPLOAD_STATUS.FAILED,
            error: cause?.raw?.message || cause?.message || '上傳失敗',
          }
          : slot
      )));
    }
  }

  function onPickFiles(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const requestGeneration = generation.current;
    const remaining = MEMORY_MEDIA_UI_LIMIT - mediaSlotsRef.current.length;
    if (remaining <= 0) {
      setFormError(`最多可選 ${MEMORY_MEDIA_UI_LIMIT} 張圖片（介面上限，非後端硬性限制）。`);
      return;
    }
    const accepted = [];
    files.slice(0, remaining).forEach((file) => {
      const validation = validateImageFile(file);
      if (validation) {
        setFormError(validation);
        return;
      }
      const localId = createLocalMediaId();
      const previewUrl = typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
        ? URL.createObjectURL(file)
        : '';
      accepted.push({
        localId,
        status: MEDIA_UPLOAD_STATUS.UPLOADING,
        file,
        previewUrl,
        objectKey: '',
        mediaId: null,
        displayOrder: mediaSlotsRef.current.length + accepted.length,
        error: '',
      });
    });
    if (!accepted.length) return;
    const next = normalizeMediaDisplayOrder([...mediaSlotsRef.current, ...accepted]);
    mediaSlotsRef.current = next;
    setMediaSlots(next);
    accepted.forEach((slot) => {
      uploadSlot(slot.localId, slot.file, requestGeneration);
    });
  }

  function removeMedia(localId) {
    setMediaSlots((current) => {
      const target = current.find((slot) => slot.localId === localId);
      if (target) revokeMediaPreview(target);
      return normalizeMediaDisplayOrder(current.filter((slot) => slot.localId !== localId));
    });
  }

  function retryMedia(localId) {
    const slot = mediaSlotsRef.current.find((item) => item.localId === localId);
    if (!slot?.file) {
      setFormError('無法重試：請重新選擇圖片。');
      return;
    }
    uploadSlot(localId, slot.file, generation.current);
  }

  function reorderMedia(localId, direction) {
    setMediaSlots((current) => moveMediaSlot(current, localId, direction));
  }

  async function submitForm(event) {
    event.preventDefault();
    const validation = validateMemoryDraft(draft, {
      mode: mode === 'create' ? 'create' : 'edit',
      mediaSlots,
    });
    if (validation) {
      setFormError(validation);
      return;
    }
    if (hasUploadingMedia(mediaSlots)) {
      setFormError('圖片仍在上傳中，請稍候再儲存。');
      return;
    }
    if (mode === 'create') {
      if (!createRequestIdRef.current) {
        createRequestIdRef.current = createMemoryClientRequestId();
      }
      const requestId = createRequestIdRef.current;
      const payload = buildCreateMemoryPayload(draft, {
        clientRequestId: requestId,
        mediaSlots,
      });
      await runMutation(async () => {
        await journeyMemoryApi.create(journey.journeyId, payload);
      }, {
        afterSuccess: () => { createRequestIdRef.current = null; },
      });
      return;
    }
    const payload = buildUpdateMemoryPayload(draft, {
      expectedRowVersion: editing.rowVersion,
      mediaSlots,
    });
    await runMutation(async () => {
      await journeyMemoryApi.update(
        journey.journeyId,
        editing.memoryEntryId,
        payload,
      );
    });
  }

  async function applyVisibility(entry, visibility) {
    if (visibility === 'PUBLIC' && !confirmVisibility) {
      setConfirmVisibility({ entry, visibility });
      return;
    }
    const target = confirmVisibility?.entry || entry;
    const next = confirmVisibility?.visibility || visibility;
    await runMutation(async () => {
      await journeyMemoryApi.changeVisibility(
        journey.journeyId,
        target.memoryEntryId,
        buildVisibilityPayload({
          visibility: next,
          expectedRowVersion: target.rowVersion,
        }),
      );
    });
  }

  async function hideEntry(entry) {
    await runMutation(async () => {
      await journeyMemoryApi.hide(
        journey.journeyId,
        entry.memoryEntryId,
        buildCasConfirmPayload({ expectedRowVersion: entry.rowVersion }),
      );
    });
  }

  async function restoreEntry(entry) {
    await runMutation(async () => {
      await journeyMemoryApi.restore(
        journey.journeyId,
        entry.memoryEntryId,
        buildCasConfirmPayload({ expectedRowVersion: entry.rowVersion }),
      );
    });
  }

  async function deleteEntry(entry) {
    if (!confirmDelete || confirmDelete.memoryEntryId !== entry.memoryEntryId) {
      setConfirmDelete(entry);
      return;
    }
    await runMutation(async () => {
      await journeyMemoryApi.delete(
        journey.journeyId,
        entry.memoryEntryId,
        buildCasConfirmPayload({ expectedRowVersion: entry.rowVersion }),
      );
    });
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <p>Loading journal…</p>
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{chromeText('notFoundTitle')}</h1>
        <Link to="/journeys">{chromeText('backToMyJourneys')}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{chromeText('forbiddenTitle')}</h1>
        <Link to="/journeys">{chromeText('backToMyJourneys')}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="journey-workspace-state" role="alert">
        <h1>無法開啟日誌</h1>
        <p>{error}</p>
        <button type="button" onClick={() => load()}>{chromeText('tryAgain')}</button>
      </main>
    );
  }

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{chromeText('eyebrow')}</p>
          <h1>{journey?.title || '未命名行程'}</h1>
          <p className="journey-workspace__meta">
            {journey?.lifecycleStatusLabel}
            {journey?.requesterRoleLabel ? ` · ${journey.requesterRoleLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{chromeText('myJourneys')}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel journey-journal" aria-labelledby="journal-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="journal-heading">日誌</h2>
        </div>

        <div className="journey-journal__toolbar">
          {allowWrite ? (
            <button type="button" disabled={busy || mode !== 'view'} onClick={startCreate}>
              寫一篇日記
            </button>
          ) : (
            <p className="journey-journal__lifecycle-note" role="status">
              只有準備出發、進行中或已完成的行程可以寫日誌。
            </p>
          )}
        </div>

        {formError ? (
          <p className="journey-journal__error" id={formErrorId} role="alert">
            {formError}
            {needsReload ? (
              <>
                {' '}
                <button type="button" disabled={busy} onClick={() => load()}>重新載入</button>
              </>
            ) : null}
          </p>
        ) : null}

        {confirmVisibility ? (
          <div className="journey-journal__confirm" role="dialog" aria-modal="true" aria-labelledby="visibility-confirm-title">
            <h3 id="visibility-confirm-title">改為公開？</h3>
            <p>公開後可能出現在公開旅程內容中（若旅程已公開）。</p>
            <div className="journey-journal__actions">
              <button type="button" disabled={busy} onClick={() => applyVisibility(confirmVisibility.entry, 'PUBLIC')}>
                {busy ? '更新中…' : '確認公開'}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmVisibility(null)}>返回</button>
            </div>
          </div>
        ) : null}

        {confirmDelete ? (
          <div className="journey-journal__confirm" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
            <h3 id="delete-confirm-title">刪除日記？</h3>
            <p>刪除後無法恢復。</p>
            <div className="journey-journal__actions">
              <button type="button" disabled={busy} onClick={() => deleteEntry(confirmDelete)}>
                {busy ? '刪除中…' : '確認刪除'}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmDelete(null)}>返回</button>
            </div>
          </div>
        ) : null}

        {(mode === 'create' || mode === 'edit') && draft ? (
          <form
            className="journey-journal__form"
            onSubmit={submitForm}
            noValidate
            aria-describedby={formError ? formErrorId : undefined}
          >
            <h3>{mode === 'create' ? '建立日記' : '編輯日記'}</h3>
            {mode === 'create' ? (
              <>
                <label htmlFor={typeId}>
                  類型
                  <select
                    id={typeId}
                    value={draft.memoryType}
                    onChange={(e) => {
                      const memoryType = e.target.value;
                      setDraft((c) => ({
                        ...c,
                        memoryType,
                        visibility: memoryType === 'PRIVATE_NOTE' ? 'PRIVATE' : c.visibility,
                      }));
                    }}
                  >
                    {MEMORY_TYPES.map((type) => (
                      <option key={type} value={type}>{memoryTypeLabel(type)}</option>
                    ))}
                  </select>
                </label>
                <fieldset className="journey-journal__fieldset">
                  <legend>可見範圍</legend>
                  {MEMORY_VISIBILITIES.map((visibility) => (
                    <label className="journey-journal__radio" key={visibility}>
                      <input
                        type="radio"
                        name="memory-visibility"
                        value={visibility}
                        checked={draft.visibility === visibility}
                        disabled={draft.memoryType === 'PRIVATE_NOTE' && visibility !== 'PRIVATE'}
                        onChange={() => setDraft((c) => ({ ...c, visibility }))}
                      />
                      <span>
                        <strong>{memoryVisibilityLabel(visibility)}</strong>
                        {' — '}
                        {memoryVisibilityHint(visibility)}
                      </span>
                    </label>
                  ))}
                </fieldset>
              </>
            ) : null}
            <label htmlFor={titleId}>
              標題（選填）
              <input
                id={titleId}
                value={draft.title}
                maxLength={200}
                onChange={(e) => setDraft((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label htmlFor={contentId}>
              內容
              <textarea
                id={contentId}
                rows={6}
                value={draft.content}
                onChange={(e) => setDraft((c) => ({ ...c, content: e.target.value }))}
                required
              />
            </label>
            <label htmlFor={occurredId}>
              發生時間
              <input
                id={occurredId}
                type="datetime-local"
                value={draft.occurredAt}
                onChange={(e) => setDraft((c) => ({ ...c, occurredAt: e.target.value }))}
                required
              />
            </label>

            <div className="journey-journal__grid-2">
              <label htmlFor={dayId}>
                綁定日期（選填）
                <select
                  id={dayId}
                  value={draft.journeyDayId || ''}
                  onChange={(e) => onDayChange(e.target.value)}
                >
                  <option value="">不綁定日期</option>
                  {(schedule?.days || []).map((day) => (
                    <option key={day.journeyDayId} value={day.journeyDayId}>
                      {formatDayOptionLabel(day)}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor={activityId}>
                綁定活動（選填）
                <select
                  id={activityId}
                  value={draft.activityId || ''}
                  onChange={(e) => onActivityChange(e.target.value)}
                >
                  <option value="">不綁定活動</option>
                  {activityOptions.map((activity) => (
                    <option key={activity.activityId} value={activity.activityId}>
                      {formatActivityOptionLabel(activity)}
                      {draft.journeyDayId ? '' : (activity.journeyDayId ? ` · Day` : ' · 未排程')}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {associationHint ? (
              <p className="journey-journal__hint" role="status">{associationHint}</p>
            ) : null}

            <div className="journey-journal__media-field">
              <label htmlFor={fileId}>照片（選填，最多 {MEMORY_MEDIA_UI_LIMIT} 張）</label>
              <input
                id={fileId}
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                multiple
                onChange={onPickFiles}
                disabled={busy || mediaSlots.length >= MEMORY_MEDIA_UI_LIMIT}
              />
              <p className="journey-journal__hint">
                僅圖片；上傳走既有 Member upload，Memory 只保存 objectKey。介面上限 {MEMORY_MEDIA_UI_LIMIT} 張（後端最多 20）。
              </p>
              {draft.visibility === 'PRIVATE' && mediaSlots.some((s) => s.status === MEDIA_UPLOAD_STATUS.UPLOADED) ? (
                <p className="journey-journal__privacy-note" role="status">{privateMemoryMediaAccessNote()}</p>
              ) : null}
              {mediaSlots.length ? (
                <ul className="journey-journal__upload-list">
                  {mediaSlots.map((slot, index) => {
                    const slotKind = memoryMediaSlotKind(slot);
                    if (slotKind !== MEMORY_MEDIA_KIND.IMAGE) {
                      // 本批不提供影片移除或排序：只顯示佔位並隨整組替換原樣保留
                      return (
                        <li key={slot.localId} className="journey-journal__upload-item" data-media-kind={slotKind}>
                          <MemoryMediaPlaceholder kind={slotKind} className="journey-journal__upload-placeholder" />
                          <div className="journey-journal__upload-meta">
                            <span>{travelT('travel.journal.media.keptOnSave')}</span>
                          </div>
                        </li>
                      );
                    }
                    const previewSrc = slot.previewUrl
                      || (slot.objectKey ? resolveMemberMediaUrl(slot.objectKey) : '');
                    return (
                      <li key={slot.localId} className="journey-journal__upload-item">
                        <SafeImage
                          className="journey-journal__upload-thumb"
                          src={previewSrc}
                          alt={`預覽圖片 ${index + 1}`}
                        />
                        <div className="journey-journal__upload-meta">
                          <span>
                            {slot.status === MEDIA_UPLOAD_STATUS.UPLOADING && '上傳中…'}
                            {slot.status === MEDIA_UPLOAD_STATUS.UPLOADED && '已上傳'}
                            {slot.status === MEDIA_UPLOAD_STATUS.FAILED && (slot.error || '上傳失敗')}
                            {slot.status === MEDIA_UPLOAD_STATUS.LOCAL_SELECTED && '準備上傳…'}
                          </span>
                          <div className="journey-journal__actions">
                            <button
                              type="button"
                              aria-label={`上移圖片 ${index + 1}`}
                              disabled={busy || index === 0}
                              onClick={() => reorderMedia(slot.localId, 'up')}
                            >
                              上移
                            </button>
                            <button
                              type="button"
                              aria-label={`下移圖片 ${index + 1}`}
                              disabled={busy || index === mediaSlots.length - 1}
                              onClick={() => reorderMedia(slot.localId, 'down')}
                            >
                              下移
                            </button>
                            {slot.status === MEDIA_UPLOAD_STATUS.FAILED ? (
                              <button
                                type="button"
                                aria-label={`重試上傳圖片 ${index + 1}`}
                                disabled={busy}
                                onClick={() => retryMedia(slot.localId)}
                              >
                                重試
                              </button>
                            ) : null}
                            <button
                              type="button"
                              aria-label={`移除圖片 ${index + 1}`}
                              disabled={busy}
                              onClick={() => removeMedia(slot.localId)}
                            >
                              移除
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>

            <MemoryStoryPreview draft={draft} mediaSlots={mediaSlots} />

            <div className="journey-journal__actions">
              <button type="submit" disabled={saveBlocked}>
                {busy ? '儲存中…' : (hasUploadingMedia(mediaSlots) ? '上傳中…' : (mode === 'create' ? '建立' : '儲存'))}
              </button>
              <button type="button" disabled={busy} onClick={cancelForm}>
                取消
              </button>
            </div>
          </form>
        ) : null}

        <section className="journey-journal__section" aria-labelledby="visible-journal-heading">
          <h3 id="visible-journal-heading">看得到的日記</h3>
          {visible.length === 0 ? (
            <div className="journey-journal__empty" role="status">
              <p>還沒有日記。旅途中的心情、觀察與私人筆記都可以記在這裡。</p>
              {allowWrite && mode === 'view' ? (
                <button type="button" disabled={busy} onClick={startCreate}>寫第一篇日記</button>
              ) : null}
            </div>
          ) : (
            <ul className="journey-journal__list">
              {visible.map((entry) => (
                <li key={entry.memoryEntryId}>
                  <MemoryArticle
                    journeyId={journeyId}
                    entry={entry}
                    viewerMemberId={viewerMemberId}
                    authorLabel={memberDisplayNameWithId(entry.authorMemberId, memberSummaries)}
                    canMutateJourney={allowWrite}
                    busy={busy}
                    sharingToChat={sharingMemoryId === entry.memoryEntryId}
                    onEdit={startEdit}
                    onShare={startShare}
                    onShareToChat={requestShareMemoryToChat}
                    onHide={hideEntry}
                    onRestore={restoreEntry}
                    onDelete={deleteEntry}
                    onVisibility={applyVisibility}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="journey-journal__section" aria-labelledby="hidden-journal-heading">
          <h3 id="hidden-journal-heading">已隱藏的日記</h3>
          {hidden.length === 0 ? (
            <p className="journey-journal__hint" role="status">沒有已隱藏的日記。</p>
          ) : (
            <ul className="journey-journal__list">
              {hidden.map((entry) => (
                <li key={entry.memoryEntryId}>
                  <MemoryArticle
                    journeyId={journeyId}
                    entry={entry}
                    viewerMemberId={viewerMemberId}
                    authorLabel={memberDisplayNameWithId(entry.authorMemberId, memberSummaries)}
                    canMutateJourney={allowWrite}
                    busy={busy}
                    sharingToChat={sharingMemoryId === entry.memoryEntryId}
                    onEdit={startEdit}
                    onShare={startShare}
                    onShareToChat={requestShareMemoryToChat}
                    onHide={hideEntry}
                    onRestore={restoreEntry}
                    onDelete={deleteEntry}
                    onVisibility={applyVisibility}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>

        {shareNotice ? (
          <p className="journey-journal__share-notice" role="status">{shareNotice}</p>
        ) : null}
        {chatShareNotice ? (
          <p className="journey-journal__share-notice" role="status">
            {chatShareNotice}
            {' '}
            <Link to={`/journeys/${journey.journeyId}/chat`}>查看聊天室</Link>
          </p>
        ) : null}

        <MemoryShareComposer
          open={Boolean(shareEntry)}
          entry={shareEntryImagesOnly}
          journeyId={journeyId}
          journeyPublicationId={journeyPublicationId}
          publicationProbePending={publicationProbePending}
          onClose={closeShare}
          onPublished={onSharePublished}
        />

        <ConfirmDialog
          open={Boolean(chatSharePending)}
          title="分享到旅程聊天室"
          description={
            chatSharePending?.isPrivate
              ? MEMORY_CHAT_SHARE_PRIVATE_DISCLOSURE
              : MEMORY_CHAT_SHARE_CONFIRM
          }
          confirmLabel="分享到聊天室"
          cancelLabel={chromeText('cancel')}
          danger={false}
          loading={Boolean(sharingMemoryId)}
          onCancel={() => {
            if (!sharingMemoryId) setChatSharePending(null);
          }}
          onConfirm={confirmShareMemoryToChat}
        />
      </section>
    </main>
  );
}
